import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../services/api';
import {
  RUBRIK_ASPEK,
  RUBRIK_KETERANGAN,
  buildEmptyRubrikState,
  computeRubrikScore,
} from '../constants/rubrik';
import './GuruRubrikPanel.css';

export default function GuruRubrikPanel() {
  const [students, setStudents] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [filterKelas, setFilterKelas] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [rubrikData, setRubrikData] = useState(buildEmptyRubrikState());
  const [refleksiData, setRefleksiData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRefleksi, setShowRefleksi] = useState(true);

  const score = useMemo(() => computeRubrikScore(rubrikData), [rubrikData]);

  const kelasOptions = useMemo(() => {
    const set = new Set(students.map((s) => s.kelas).filter(Boolean));
    return [...set].sort();
  }, [students]);

  const loadStudents = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams();
      if (searchQ.trim()) params.set('q', searchQ.trim());
      if (filterKelas) params.set('kelas', filterKelas);
      const { data } = await apiClient.get(`/rubrik/students?${params.toString()}`);
      setStudents(data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal memuat daftar siswa');
      setStudents([]);
    } finally {
      setLoadingList(false);
    }
  }, [searchQ, filterKelas]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const loadStudentDetail = useCallback(async (userId) => {
    if (!userId) return;
    setLoadingDetail(true);
    setSelectedId(userId);
    try {
      const { data } = await apiClient.get(`/rubrik/students/${userId}`);
      const student = data.student;
      setRefleksiData(data.refleksi);
      setRubrikData(
        data.rubrik
          ? { ...buildEmptyRubrikState(), ...data.rubrik }
          : buildEmptyRubrikState({
              namaSiswa: student.nama,
              kelas: student.kelas || '',
            })
      );
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal memuat rubrik siswa');
      setSelectedId(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const handleRubrikChange = (e) => {
    const { name, value } = e.target;
    setRubrikData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const { data } = await apiClient.put(`/rubrik/students/${selectedId}`, { data: rubrikData });
      toast.success(data.message || 'Rubrik berhasil disimpan');
      loadStudents();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menyimpan rubrik');
    } finally {
      setSaving(false);
    }
  };

  const selectedStudent = students.find((s) => String(s.id) === String(selectedId));

  return (
    <div className="guru-rubrik-panel">
      <aside className="guru-rubrik-sidebar">
        <div className="guru-rubrik-sidebar-header">
          <h3>👨‍🏫 Penilaian Siswa</h3>
          <p>Pilih siswa untuk menilai rubrik & refleksi</p>
        </div>

        <div className="guru-rubrik-filters">
          <input
            type="search"
            placeholder="Cari nama siswa..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="guru-rubrik-input"
          />
          <select
            value={filterKelas}
            onChange={(e) => setFilterKelas(e.target.value)}
            className="guru-rubrik-input"
          >
            <option value="">Semua kelas</option>
            {kelasOptions.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>

        <div className="guru-rubrik-student-list">
          {loadingList ? (
            <p className="guru-rubrik-muted">Memuat siswa...</p>
          ) : students.length === 0 ? (
            <p className="guru-rubrik-muted">Tidak ada siswa ditemukan.</p>
          ) : (
            students.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`guru-rubrik-student-item ${String(selectedId) === String(s.id) ? 'active' : ''}`}
                onClick={() => loadStudentDetail(s.id)}
              >
                <span className="student-name">{s.nama}</span>
                <span className="student-meta">{s.kelas || '—'} · {s.namaSekolah || '—'}</span>
                <span className="student-badges">
                  {s.hasRubrik ? (
                    <span className="badge done">{s.nilaiAkhir ?? '—'}%</span>
                  ) : (
                    <span className="badge pending">Belum dinilai</span>
                  )}
                  {s.hasRefleksi && <span className="badge refleksi">Refleksi ✓</span>}
                </span>
              </button>
            ))
          )}
        </div>
      </aside>

      <main className="guru-rubrik-main">
        {!selectedId ? (
          <div className="guru-rubrik-empty">
            <div className="empty-icon">📋</div>
            <h3>Pilih siswa dari daftar</h3>
            <p>Nilai rubrik digital storytelling dan beri catatan untuk siswa.</p>
          </div>
        ) : loadingDetail ? (
          <p className="guru-rubrik-muted">Memuat data siswa...</p>
        ) : (
          <>
            <div className="guru-rubrik-main-header">
              <div>
                <h3>{selectedStudent?.nama || rubrikData.namaSiswa}</h3>
                <p>{rubrikData.kelas || selectedStudent?.kelas} · {selectedStudent?.namaSekolah}</p>
              </div>
              <div className="guru-rubrik-actions">
                <button type="button" className="guru-btn save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Menyimpan...' : '💾 Simpan Penilaian'}
                </button>
              </div>
            </div>

            {refleksiData && (
              <section className="guru-refleksi-card">
                <button
                  type="button"
                  className="guru-refleksi-toggle"
                  onClick={() => setShowRefleksi((v) => !v)}
                >
                  🪞 Refleksi Siswa {showRefleksi ? '▾' : '▸'}
                </button>
                {showRefleksi && (
                  <div className="guru-refleksi-body">
                    {refleksiData.fact && <p><strong>Fact:</strong> {refleksiData.fact}</p>}
                    {refleksiData.feeling && <p><strong>Feeling:</strong> {refleksiData.feeling}</p>}
                    {refleksiData.finding && <p><strong>Finding:</strong> {refleksiData.finding}</p>}
                    {refleksiData.future && <p><strong>Future:</strong> {refleksiData.future}</p>}
                  </div>
                )}
              </section>
            )}

            <div className="rubrik-form-container guru-rubrik-form">
              <div className="rubrik-student-info">
                <div className="form-group">
                  <label htmlFor="guru-namaSiswa">Nama Siswa</label>
                  <input id="guru-namaSiswa" name="namaSiswa" value={rubrikData.namaSiswa} onChange={handleRubrikChange} readOnly />
                </div>
                <div className="form-group">
                  <label htmlFor="guru-kelas">Kelas</label>
                  <input id="guru-kelas" name="kelas" value={rubrikData.kelas} onChange={handleRubrikChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="guru-judulVideo">Judul Video / Karya</label>
                  <input id="guru-judulVideo" name="judulVideo" value={rubrikData.judulVideo} onChange={handleRubrikChange} placeholder="Judul karya yang dinilai" />
                </div>
                <div className="form-group">
                  <label htmlFor="guru-tanggal">Tanggal Penilaian</label>
                  <input id="guru-tanggal" type="date" name="tanggalPenilaian" value={rubrikData.tanggalPenilaian} onChange={handleRubrikChange} />
                </div>
              </div>

              <div className="rubrik-table-container">
                <table className="rubrik-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Aspek</th>
                      <th>Indikator</th>
                      {[1, 2, 3, 4].map((n) => (
                        <th key={n}>{n}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RUBRIK_ASPEK.map((aspek) =>
                      aspek.indikator.map((ind, i) => (
                        <tr key={ind.key}>
                          {i === 0 && (
                            <>
                              <td rowSpan={aspek.indikator.length}>{aspek.no}</td>
                              <td rowSpan={aspek.indikator.length} className="aspek-name">{aspek.nama}</td>
                            </>
                          )}
                          <td className="indikator-label">{ind.label}</td>
                          {[1, 2, 3, 4].map((n) => (
                            <td key={n} className="skor-cell">
                              <label className="skor-radio">
                                <input
                                  type="radio"
                                  name={ind.key}
                                  value={String(n)}
                                  checked={String(rubrikData[ind.key]) === String(n)}
                                  onChange={handleRubrikChange}
                                />
                              </label>
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="rubrik-konversi">
                <strong>Keterangan skor:</strong>
                <ul>
                  {RUBRIK_KETERANGAN.map((k) => (
                    <li key={k.skor}><strong>{k.skor}</strong> — {k.label}</li>
                  ))}
                </ul>
              </div>

              <div className="rubrik-results guru-rubrik-results">
                <div className="result-card">
                  <span className="result-label">Total Skor</span>
                  <span className="result-value">{score.totalSkor} / 48</span>
                </div>
                <div className="result-card">
                  <span className="result-label">Nilai Akhir</span>
                  <span className="result-value">{score.nilaiAkhir}%</span>
                </div>
                <div className="result-card">
                  <span className="result-label">Keterangan</span>
                  <span className="result-value">{score.keterangan}</span>
                </div>
              </div>

              <div className="rubrik-notes">
                <label htmlFor="guru-catatan">Catatan Guru</label>
                <textarea
                  id="guru-catatan"
                  name="catatanGuru"
                  rows={4}
                  value={rubrikData.catatanGuru}
                  onChange={handleRubrikChange}
                  placeholder="Masukkan umpan balik dan saran untuk siswa..."
                />
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
